import React from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import useAuth from "../../../../hooks/useAuth";
import BaseInfoModal from "./BaseInfoModal";
import QuantityRow from "./QuantityRow";
import useModalStates from "./hooks/useModalStates";
import AllocatedQuantityInfoModal from "./AllocatedQuantityInfoModal";
import InCartQuantityInfoModal from "./InCartQuantityInfoModal";
import ReservedQuantityInfoModal from "./ReservedQuantityInfoModal";
import RecalculateAvailableQuantityButton from "./RecalculateAvailableQuantityButton";

const getReferenceItemsTotalAvailable = asset => asset?.groupedMemberAssets?.total_available_quantity || 0;

export default function GroupedQuantityCalculationModal({
  open,
  asset,
  onClose,
  hideInfoButtons = false,
}) {
  const { user } = useAuth();
  const { settings } = user.company;
  const quantityLabel = settings.quantity_label || "Quantity";
  const modalStates = useModalStates(["allocated", "reserved", "inCart"]);
  const isAdminOrManager = ["admin", "manager"].includes(user.role);
  const showInfoButtons = isAdminOrManager && !hideInfoButtons;
  const reservedQuantity = Number(asset?.reservedQuantity || 0);

  return (
    <BaseInfoModal
      open={open}
      onClose={onClose}
      title={`${quantityLabel} Calculation`}
      maxWidth="xs"
      titleAction={<RecalculateAvailableQuantityButton asset={asset} />}
    >
      <Box variant="body2" color="text.secondary" sx={{ mb: 3, px: 3, textAlign: "center" }}>
        This screen shows you how {quantityLabel.toLowerCase()} was calculated for this
        grouped item.
      </Box>
      <Box display="flex" flexDirection="column" gap={2}>
        <QuantityRow
          value={getReferenceItemsTotalAvailable(asset)}
          label="Quantity in Warehouse"
          showInfoButton={false}
        />
        <QuantityRow
          value={asset.inCartQuantity}
          label="Quantity in Cart"
          operator="minus"
          onInfoClick={modalStates.inCart.open}
          showInfoButton={showInfoButtons}
        />
        {settings.manual_location_checkout && (
          <QuantityRow
            value={asset.allocatedQuantity}
            label="Quantity to Be Picked"
            operator="minus"
            onInfoClick={modalStates.allocated.open}
            showInfoButton={showInfoButtons}
          />
        )}
        {settings.reservations && reservedQuantity !== 0 && (
          <QuantityRow
            value={reservedQuantity}
            label="Reserved Quantity"
            operator="minus"
            onInfoClick={modalStates.reserved.open}
            showInfoButton={showInfoButtons}
          />
        )}
        <Divider sx={{ borderColor: "rgba(0, 0, 0, 0.5)" }} />
        <QuantityRow
          value={asset.availableQuantity}
          label="Available Quantity"
          operator="equals"
          showInfoButton={false}
        />
      </Box>

      {showInfoButtons && modalStates.allocated.isOpen && (
        <AllocatedQuantityInfoModal asset={asset} onClose={modalStates.allocated.close} />
      )}
      {showInfoButtons && modalStates.reserved.isOpen && (
        <ReservedQuantityInfoModal asset={asset} onClose={modalStates.reserved.close} />
      )}
      {showInfoButtons && modalStates.inCart.isOpen && (
        <InCartQuantityInfoModal asset={asset} onClose={modalStates.inCart.close} />
      )}
    </BaseInfoModal>
  );
}
